import type { ReactNode } from "react";
import { Button } from "../ui/button";
import { Edit, Trash2 } from "lucide-react";
import type { TableColumnDef } from "./types";

export interface ListMasterRow {
  id: string | number;
  name: string;
  code?: string | null;
  is_active?: boolean;
}

interface ListMasterTableOptions<T extends ListMasterRow> {
  currentPage: number;
  pageSize: number;
  onEdit: (row: T) => void;
  onDelete: (row: T) => void;
  /** Extra columns inserted before code/status/actions */
  extraColumns?: TableColumnDef<T>[];
  statusLabel?: (row: T) => string;
  /** Include code column (default true) */
  showCode?: boolean;
  /** Include status column (default true) */
  showStatus?: boolean;
  /** Override default name cell renderer */
  renderNameCell?: (row: T) => ReactNode;
  /** Override name column header (default: Name) */
  nameColumnLabel?: string;
}

/** Standard list-master columns with column display support */
export function buildListMasterTableColumns<T extends ListMasterRow>({
  currentPage,
  pageSize,
  onEdit,
  onDelete,
  extraColumns = [],
  statusLabel,
  showCode = true,
  showStatus = true,
  renderNameCell,
  nameColumnLabel = "Name",
}: ListMasterTableOptions<T>): TableColumnDef<T>[] {
  return [
    {
      id: "sr",
      label: "#",
      width: 56,
      alwaysVisible: true,
      lockVisibility: true,
      renderCell: (_row, rowIndex) =>
        (currentPage - 1) * pageSize + rowIndex + 1,
    },
    {
      id: "name",
      label: nameColumnLabel,
      width: 200,
      renderCell: row =>
        renderNameCell?.(row) ?? (
          <span className="font-medium">{row.name}</span>
        ),
    },
    ...extraColumns,
    ...(showCode
      ? [
          {
            id: "code",
            label: "Code",
            width: 100,
            defaultVisible: true,
            renderCell: (row: T) => (row as ListMasterRow).code ?? "—",
          } satisfies TableColumnDef<T>,
        ]
      : []),
    ...(showStatus
      ? [
          {
            id: "status",
            label: "Status",
            width: 100,
            renderCell: (row: T) =>
              statusLabel?.(row) ??
              ((row as ListMasterRow).is_active !== false
                ? "Active"
                : "Inactive"),
          } satisfies TableColumnDef<T>,
        ]
      : []),
    {
      id: "actions",
      label: "Actions",
      width: 120,
      renderCell: row => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(row)}
            aria-label="Edit"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-destructive hover:text-destructive"
            onClick={() => onDelete(row)}
            aria-label="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];
}
